import { useState, useCallback, useRef, useMemo, useEffect, useId } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import IconButton from 'LuxKit/Button/IconButton'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import SolidMicrophoneIcon from 'LuxKit/Icons/solid/SolidMicrophoneIcon'
import Group from 'components/utils/Group'
import { normaliseMessage } from 'tripPlanner/api/chatUtils'
import { useMicrophoneTranscriber } from 'tripPlanner/components/Chat/hooks/useMicrophoneTranscriber'
import RecordingOverlay from 'tripPlanner/components/Chat/RecordingOverlay'
import BorderlessTextAreaOnly from 'components/Common/Form/Input/BorderlessTextAreaOnly'
import ChatInputShell from 'tripPlanner/components/Chat/ChatInputShell'
import MorphingSendButton from 'tripPlanner/components/Chat/MorphingSendButton'
import { mediaQueryUp } from 'components/utils/breakpoint'

const InputSizer = styled.div`
  width: 60%;
  max-width: min(100%, ${rem(600)});
  min-width: 0;
  box-sizing: border-box;
  transition: width 0.25s ease, max-width 0.25s ease;

  &.active {
    width: 100%;
    max-width: min(100%, ${rem(652)});
  }

  &.force-expanded {
    width: 100%;
    max-width: min(100%, ${rem(652)});
  }

  ${mediaQueryUp.tablet} {
    &.force-expanded:not(.active) {
      width: 60%;
      max-width: min(100%, ${rem(600)});
    }
  }
`

const TextAreaWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  width: 100%;

  > * {
    width: 100%;
    min-width: 0;
  }

  textarea {
    &:placeholder-shown {
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`

interface Props {
  onSubmit: (message: string) => void | Promise<void>
  disabled?: boolean
  placeholder?: string
  hasActiveSession?: boolean
  forceExpanded?: boolean
}

function DrawerAskAIInput({
  onSubmit,
  disabled = false,
  placeholder: placeholderProp,
  hasActiveSession = false,
  forceExpanded = false,
}: Props) {
  const messageFieldId = useId()
  const intl = useIntl()
  const placeholder = placeholderProp ?? intl.formatMessage({
    id: '8v8W1PlItxkd_1u8oc57',
    defaultMessage: 'Ask about this...',
    description: 'Default placeholder text for AI chat input in trip planner drawer',
  })
  const [message, setMessage] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const latestMessageRef = useRef('')
  const shouldSubmitTranscriptionRef = useRef(false)
  const hasMessage = Boolean(message.trim())
  const isActive = isFocused || hasMessage || hasActiveSession

  useEffect(() => {
    latestMessageRef.current = message
  }, [message])

  const handleTranscription = useCallback((text: string) => {
    const currentMessage = latestMessageRef.current
    const separator = currentMessage.trim() ? ' ' : ''
    const nextMessage = currentMessage + separator + text

    if (shouldSubmitTranscriptionRef.current) {
      shouldSubmitTranscriptionRef.current = false
      const processed = normaliseMessage(nextMessage)
      if (disabled) {
        setMessage(nextMessage)
        return
      }
      if (!processed) {
        setMessage(nextMessage)
        return
      }
      onSubmit(processed)
      setMessage('')
      textareaRef.current?.focus()
      return
    }

    setMessage(nextMessage)
  }, [disabled, onSubmit])

  const handleAfterTranscription = useCallback(() => {
    shouldSubmitTranscriptionRef.current = false
  }, [])

  const {
    isRecording,
    isTranscribing,
    recordingDuration,
    waveformLevels,
    filledBars,
    startRecording,
    cancelRecording,
    submitRecording,
  } = useMicrophoneTranscriber({
    onTranscription: handleTranscription,
    onAfterTranscription: handleAfterTranscription,
  })

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleSubmit = useCallback(() => {
    if (disabled || !hasMessage) return
    const processed = normaliseMessage(message)
    onSubmit(processed)
    setMessage('')
    textareaRef.current?.focus()
  }, [message, onSubmit, disabled, hasMessage])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value)
  }, [])

  const handleBlur = useCallback(() => {
    setIsFocused(false)
  }, [])

  const handleRecordingCancel = useCallback(() => {
    shouldSubmitTranscriptionRef.current = false
    cancelRecording()
  }, [cancelRecording])

  const handleRecordingSubmit = useCallback(() => {
    shouldSubmitTranscriptionRef.current = true
    submitRecording()
  }, [submitRecording])

  const microphoneButton = useMemo(() => {
    return (
      <IconButton
        kind="tertiary"
        shape="circle"
        size="small"
        onClick={startRecording}
        disabled={disabled || isTranscribing}
      >
        {isTranscribing ? <LineSpinnerIcon /> : <SolidMicrophoneIcon />}
      </IconButton>
    )
  }, [disabled, isTranscribing, startRecording])

  const shouldShowOverlay = isRecording || isTranscribing

  const recordingOverlay = useMemo(() => {
    if (!shouldShowOverlay) return null

    return (
      <RecordingOverlay
        placeholder={placeholder}
        waveformLevels={waveformLevels}
        filledBars={filledBars}
        durationMs={recordingDuration}
        onCancel={handleRecordingCancel}
        onSubmit={handleRecordingSubmit}
        isTranscribing={isTranscribing}
      />
    )
  }, [
    filledBars,
    handleRecordingCancel,
    handleRecordingSubmit,
    isTranscribing,
    placeholder,
    recordingDuration,
    shouldShowOverlay,
    waveformLevels,
  ])

  return (
    <InputSizer className={clsx({ active: isActive, 'force-expanded': forceExpanded })}>
      <ChatInputShell compact={!isActive}>
        <Group direction="horizontal" gap={8} verticalAlign="center" fullWidth>
          <TextAreaWrapper>
            <BorderlessTextAreaOnly
              ref={textareaRef}
              id={messageFieldId}
              name="tripPlannerDrawerAskAiMessage"
              placeholder={placeholder}
              value={message}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </TextAreaWrapper>
          {microphoneButton}
          <MorphingSendButton
            expanded={hasMessage}
            onClick={handleSubmit}
            disabled={disabled || !hasMessage}
          />
          {recordingOverlay}
        </Group>
      </ChatInputShell>
    </InputSizer>
  )
}

DrawerAskAIInput.displayName = 'DrawerAskAIInput'

export default DrawerAskAIInput
